import { formatAmount, numberLocaleFor } from '../lib/money';
import { useState, useEffect, useCallback, useRef } from 'react';
import { useCheckoutStore } from '../store/checkout';
import type { Address } from '../store/checkout';
import { getMarketConfig } from '../config/market';
import { Button } from '../components/Button';
import styles from './PaymentStep.module.css';
import type { PaymentStatusData, SurfboardOnlineSDK, SurfboardPaymentMethod } from '../types/surfboard';
import { getPaymentMethods, startRedirectPayment } from '../api/client';

const LOGOS = {
  visa: 'https://res.cloudinary.com/martinsurf/image/upload/v1764765667/surfboardmerchant/hpxkzcghdvo2duf4a9cm.png',
  mastercard: 'https://res.cloudinary.com/martinsurf/image/upload/v1764765654/surfboardmerchant/e4knt66x4mjaspstalbn.png',
  vipps: 'https://res.cloudinary.com/martinsurf/image/upload/v1764765541/surfboardmerchant/b1x670kyqav9izugemri.png',
  klarna: 'https://res.cloudinary.com/martinsurf/image/upload/v1764765566/surfboardmerchant/yksx3mcpjg7q0zaina7d.png',
  swish: 'https://www.surfboardpayments.com/img/methods/swish.svg',
};

type Method = 'card' | 'vipps' | 'klarna' | 'swish' | null;

// =====================================================
// VERSION 20: SINGLE ORDER — CLEAN
//
// One wallet order (no enforceTokenization) for ALL payment methods.
// Card form, Apple Pay, Google Pay, Vipps, Klarna — all on same order.
// No tokenization, no saved cards, no SDK switching.
// =====================================================

let globalSdkInitialized = false;
let globalSdkInitAttempted = false;   // blocks ALL instances once first attempt starts
let globalCallbacksRegistered = false;

// Module-level callback forwarders — survive component remounts.
// SDK callbacks (registered once on first mount) call these;
// each new component mount updates them to point to its own handlers.
let activePaymentStatusHandler: ((data: PaymentStatusData) => void) | null = null;
let activeErrorHandler: ((code: string, msg: string) => void) | null = null;

/**
 * In-flight remount, shared across component instances.
 *
 * Two overlapping remountOnlineSDK calls make the SDK backend return 500, so
 * a second caller must await the first rather than starting its own.
 */
let remountInFlight: Promise<void> | null = null;

/** Remount with single-flight and a bound on how long a hung CDN can freeze checkout. */
function remountSdkOnce(
  SDK: SurfboardOnlineSDK,
  config: { publicKey: string; orderId: string; nonce: string }
): Promise<void> {
  if (remountInFlight) return remountInFlight;

  const attempt = Promise.race([
    Promise.resolve(SDK.remountOnlineSDK!(config)).then(() => undefined),
    new Promise<never>((_, reject) =>
      setTimeout(() => reject(new Error('SDK remount timed out after 15s')), 15000)
    ),
  ]).finally(() => { remountInFlight = null; });

  remountInFlight = attempt;
  return attempt;
}

/**
 * Mount the card widget, tolerating a container that is not ready yet.
 *
 * Two separate causes produce the same `null is not an object (evaluating
 * t.firstChild)` crash:
 *
 *  1. The container still holds the dead iframe from a previous attempt.
 *     Surfboard mounts by reaching into the node, and stale content makes it
 *     read a child that is no longer what it expects. Clearing first is the
 *     actual fix for retry-after-failure.
 *
 *  2. The node is not in the document yet, or React replaced it during a
 *     re-render so the reference points at a detached element. Mounting into a
 *     detached node fails the same way — hence the fresh lookup every attempt,
 *     never a cached element.
 *
 * firstChild/null errors get a longer, growing backoff because they are the
 * "not ready yet" case; anything else is retried briefly and then given up on.
 */
async function mountCardWidget(SDK: SurfboardOnlineSDK, containerId: string): Promise<boolean> {
  const MAX_NOT_READY = 12;
  const MAX_OTHER = 5;

  for (let attempt = 0; attempt < MAX_NOT_READY; attempt++) {
    // Fresh every time — a reference held across a re-render may be detached.
    const el = document.getElementById(containerId);

    if (el) {
      // Never mount into a container that still holds the previous iframe.
      el.innerHTML = '';
      try {
        SDK.mount({ mountCardWidget: containerId });
        return true;
      } catch (e) {
        const msg = e instanceof Error ? e.message : String(e);
        const notReady = msg.includes('firstChild') || msg.includes('null');
        if (!notReady && attempt >= MAX_OTHER) {
          console.error('[SagaPay:Payment] Card mount failed permanently:', msg);
          return false;
        }
        console.warn(`[SagaPay:Payment] Card mount attempt ${attempt + 1} failed: ${msg}`);
      }
    }

    await new Promise((r) => setTimeout(r, Math.min(500 + attempt * 200, 2000)));
  }

  console.error('[SagaPay:Payment] Card mount gave up after', MAX_NOT_READY, 'attempts');
  return false;
}

export function resetSdkState() {
  globalSdkInitialized = false;
  globalSdkInitAttempted = false;
  // Only reset callbacks if the SDK hasn't been loaded yet.
  // Once callbacks are registered on the Surfboard SDK singleton,
  // they CANNOT be re-registered (ON_005). The module-level
  // forwarder pattern makes this safe — handlers point to the
  // current component instance regardless of remounts.
  if (!window.SurfboardOnlineSDK) {
    globalCallbacksRegistered = false;
  }
  activePaymentStatusHandler = null;
  activeErrorHandler = null;
}

/**
 * Dial codes for the markets the checkout serves.
 *
 * The SDK wants `{ countryCode, number }` where countryCode is the dial prefix,
 * and both call sites hardcoded '+47'. A Swedish customer's number therefore
 * reached Surfboard as a Norwegian one — which is not merely untidy: it is the
 * number the 3-D Secure SMS goes to, and the one the shop rings when a delivery
 * fails.
 */
const DIAL_CODES: Record<string, string> = { NO: '+47', SE: '+46', DK: '+45', FI: '+358' };

/**
 * Split a typed phone number into dial code and national number.
 *
 * Only a number the customer wrote in international form — leading `+` or `00`
 * — is treated as carrying its own country. Guessing from the digits alone does
 * not work: the Norwegian number 45 12 34 56 begins with Denmark's dial code.
 */
function splitPhone(
  raw: string | undefined,
  market: string | undefined
): { countryCode: string; number: string } | undefined {
  const trimmed = (raw || '').trim();
  const digits = trimmed.replace(/[^\d]/g, '');
  if (!digits) return undefined;

  if (trimmed.startsWith('+') || digits.startsWith('00')) {
    const bare = digits.replace(/^00/, '');
    // Longest prefix first, so +358 is not read as +35.
    for (const dial of ['+358', '+47', '+46', '+45']) {
      const prefix = dial.slice(1);
      if (bare.startsWith(prefix)) return { countryCode: dial, number: bare.slice(prefix.length) };
    }
    // Somewhere we do not serve. Most European codes are two digits — a guess,
    // but a better one than declaring the number Norwegian.
    return { countryCode: `+${bare.slice(0, 2)}`, number: bare.slice(2) };
  }

  return { countryCode: DIAL_CODES[(market || 'NO').toUpperCase()] ?? '+47', number: digits };
}

/**
 * A checkout address in the shape the SDK takes, with nothing dropped.
 *
 * The previous mapping sent street, postcode, city and country and stopped
 * there, so the C/O line and the second address line — both collected on the
 * form, both stored on the session — never reached the order.
 */
/**
 * The name to hand the SDK, which takes one string and splits it upstream.
 *
 * Surfboard cuts at the first space: everything after it becomes the surname.
 * So "Kari Mellomnavn Nordmann" is filed under the surname "Mellomnavn
 * Nordmann" — the customer's middle name promoted onto their family name, on
 * the order and on anything printed from it.
 *
 * When the split is known — the shop stated it, or the address form collected
 * it — send exactly two parts so that cut lands where it should. The middle
 * name is dropped rather than misfiled, which is the better of the two losses:
 * a surname is what an order is matched and addressed by, and our own API keeps
 * the full name intact for anyone who wants it.
 *
 * Sending the parts separately is not an option — `SurfboardCustomerDetails`
 * has a single `name` string, and the structured name we send over REST is
 * discarded upstream along with every address.
 */
function sdkName(c: { name?: string; firstName?: string; lastName?: string } | null | undefined) {
  if (!c) return undefined;
  if (c.firstName && c.lastName) return `${c.firstName} ${c.lastName}`;
  return c.name || undefined;
}

function sdkAddress(a: Address | null | undefined) {
  if (!a) return undefined;
  return {
    addressLine1: a.streetAddress,
    ...(a.addressLine2 ? { addressLine2: a.addressLine2 } : {}),
    ...(a.careOf ? { careOf: a.careOf } : {}),
    postalCode: a.postalCode,
    city: a.city,
    countryCode: a.countryCode || 'NO',
  };
}

export function PaymentStep() {
  const {
    prevStep, cart, surfboardOrderId, surfboardNonce, publicKey,
    customer, shippingAddress, billingAddress, merchant, sessionId, completeCheckout,
    selectedShipping, setSurfboardData
  } = useCheckoutStore();

  const locale = useCheckoutStore((s) => s.locale);
  const market = getMarketConfig(locale);
  const L = market.labels;

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [processing, setProcessing] = useState(false);
  const [processingText, setProcessingText] = useState(L.processingLabel || 'Behandler...');
  const [selected, setSelected] = useState<Method>(null);
  const [lastUsedMethod, setLastUsedMethod] = useState<Method>(null);
  const [sdkReady, setSdkReady] = useState(false);

  // Wallet button availability
  const [applePayAvailable, setApplePayAvailable] = useState(false);
  const [googlePayAvailable, setGooglePayAvailable] = useState(false);
  const [walletCheckDone, setWalletCheckDone] = useState(false);

  // Swish QR code (desktop flow)
  const [swishQRUrl, setSwishQRUrl] = useState<string | null>(null);

  const initStarted = useRef(false);
  const retryCount = useRef(0);
  const MAX_RETRIES = 2;
  const cardMounted = useRef(false);
  const walletMounted = useRef(false);
  const paymentInFlight = useRef(false);
  const inFlightWatchdog = useRef<ReturnType<typeof setTimeout> | null>(null);
  const mountPhaseDone = useRef(false);
  /** Set when the last failure was "this store cannot take that method". */
  const methodUnavailable = useRef(false);
  /**
   * Ask the server for a brand-new payment order on the next fetch.
   *
   * A SagaPay order has one life: once a payment against it has failed, it is
   * spent, and a second attempt on the same order cannot succeed. So any path
   * that lets the customer try again has to replace the order first.
   */
  const forceNewOrder = useRef(false);

  /**
   * Methods the store turned out not to support, learnt the hard way.
   *
   * A terminal can advertise KLARNA while the merchant has no Klarna
   * agreement; nothing says so until the payment is initiated and the API
   * answers PS_0002. Rather than leaving the customer on an option that can
   * never work, we take it off the list and let them pick another.
   */
  const [unavailableMethods, setUnavailableMethods] = useState<string[]>([]);

  /**
   * A failure from pressing Pay, kept apart from load-time errors.
   *
   * The load-time banner is deliberately hidden once the order exists, because
   * a hiccup that later resolved should not sit above a working payment step.
   * That condition also swallowed every pay-time failure: the customer pressed
   * Betal, nothing happened, and no message ever appeared. This one always
   * renders.
   */
  const [payError, setPayError] = useState<{ message: string; canRetry: boolean } | null>(null);

  // A message left by the return from Vipps/Klarna, set before this step
  // mounted. Consumed once so it does not reappear on a later render.
  const paymentNotice = useCheckoutStore((s) => s.paymentNotice);
  const setPaymentNotice = useCheckoutStore((s) => s.setPaymentNotice);
  useEffect(() => {
    if (!paymentNotice) return;
    setPayError({ message: paymentNotice, canRetry: false });
    setPaymentNotice(null);
  }, [paymentNotice, setPaymentNotice]);

  // Available methods from merchant config
  const methods = (merchant?.activatedPaymentMethods || []).filter(
    m => !unavailableMethods.includes(m)
  );
  const cartCurrency = cart?.totalAmount?.currency || 'NOK';
  const hasCard = methods.some(m => ['CARD', 'VISA', 'MASTERCARD', 'CPOC'].includes(m));
  const hasVipps = methods.some(m => ['VIPPS', 'SVIPPS'].includes(m)) && cartCurrency === 'NOK';
  const hasKlarna = methods.includes('KLARNA');
  const hasSwish = methods.some(m => ['SWISH', 'NSWISH', 'SSWISH'].includes(m)) && cartCurrency === 'SEK';
  // Apple Pay & Google Pay are CARD terminal features in Surfboard SDK.
  // When CARD is active, the SDK can mount wallet buttons — browser capability
  // checks (ApplePaySession.canMakePayments / Google Pay API) determine
  // if the buttons actually render. We just need to provide the container.
  const hasApplePay = methods.some(m => ['APPLE_PAY', 'APPLEPAY', 'CPOC', 'CARD'].includes(m));
  const hasGooglePay = methods.some(m => ['GOOGLE_PAY', 'GOOGLEPAY', 'CPOC', 'CARD'].includes(m));

  /**
   * Throw away the current order and the mounted card form, and build both
   * again from scratch.
   *
   * `forceNewOrder` is what makes this work. The fetch below used to decide
   * whether to ask for a new order from `retryCount`, which the retry button
   * reset to 0 on its way in — so pressing "Prøv igjen" quietly re-used the
   * dead order and the second attempt could never succeed.
   */
  const rebuildOrderAndCardForm = useCallback(() => {
    forceNewOrder.current = true;
    retryCount.current = 0;
    globalSdkInitialized = false;
    globalSdkInitAttempted = false;
    initStarted.current = false;
    walletMounted.current = false;
    cardMounted.current = false;
    setSdkReady(false);
    setWalletCheckDone(false);
    setSurfboardData('', '', '');
  }, [setSurfboardData]);

  // Manual retry — user presses retry button after all auto-retries fail
  const handleManualRetry = useCallback(() => {
    setError(null);
    setPayError(null);
    setLoading(true);
    rebuildOrderAndCardForm();
  }, [rebuildOrderAndCardForm]);

  // Fetch SDK credentials if missing (lazy order creation)
  useEffect(() => {
    if (!sessionId || (surfboardOrderId && surfboardOrderId.length > 0)) return;
    // If retrying, force server to create a brand-new Surfboard order
    const forceNew = forceNewOrder.current || retryCount.current > 0;
    forceNewOrder.current = false;

    /**
     * Fetch the SDK credentials, retrying a transient failure automatically.
     *
     * This call creates the payment order; without it surfboardOrderId stays
     * null and the customer cannot pay at all. A single hiccup here used to
     * dead-end them behind a "Prøv igjen" button on an otherwise complete
     * checkout — the last place in the funnel where you want to lose someone,
     * and the most likely place to hiccup when traffic peaks.
     *
     * Retries only the load, never a payment: no charge has been attempted at
     * this point, so re-requesting is always safe.
     */
    const fetchWithRetry = async (attempt = 0): Promise<Awaited<ReturnType<typeof getPaymentMethods>>> => {
      const res = await getPaymentMethods(sessionId, null, forceNew || attempt > 0);
      const usable = res.data?.surfboardOrderId && res.data?.surfboardNonce && res.data?.publicKey;
      if (usable || attempt >= 2) return res;
      await new Promise((r) => setTimeout(r, 400 * (attempt + 1)));
      return fetchWithRetry(attempt + 1);
    };

    fetchWithRetry().then(response => {
      if (response.data) {
        const { surfboardOrderId: orderId, surfboardNonce: nonce, publicKey: key, lastUsedMethod: preferredMethod } = response.data;
        if (orderId && nonce && key) {
          // Clear any error from an earlier attempt. Without this a transient
          // failure left its banner — "Kunne ikke koble til serveren" with a
          // retry button — sitting above a payment step that had since loaded
          // fine, telling the customer their order was broken when it wasn't.
          setError(null);
          setSurfboardData(orderId, nonce, key);
        } else {
          setError(L.paymentDataError);
        }

        // The SDK computes CHECK_PARAM from the domain it is actually running
        // on, and that is left alone on purpose. We used to override it with
        // the store's registered domain so the checkout could run anywhere,
        // which meant CHECK_PARAM encrypted a domain the page was not served
        // from. Surfboard's SDK is domain-locked: the fix is to serve the
        // checkout from the registered domain, not to tell the SDK otherwise.
        /**
         * Only meaningful when the checkout runs on the merchant's own domain.
         *
         * On the hosted checkout the page is served from checkout.sagapay.no
         * and the store's registered PaymentPage URL is the shop's own site, so
         * these never match — and the payment goes through regardless. The
         * warning therefore fired on every single hosted payment, announcing a
         * rejection that did not happen, which is worse than saying nothing:
         * the one case where it is real is the embedded widget, and by then
         * everybody has learned to scroll past it.
         */
        const serverDomain = (response.data as unknown as Record<string, unknown>).merchantDomain as string | undefined;
        const embedded = Boolean((window as unknown as Record<string, unknown>).__SAGAPAY_WIDGET_MODE__);
        if (embedded && serverDomain && serverDomain !== window.location.host) {
          console.warn(
            '[SagaPay:Payment] Store is registered for',
            serverDomain,
            'but the embedded checkout is running on',
            window.location.host,
            '— the SDK is domain-locked and the gateway may reject this. Register this host with Surfboard.'
          );
        }


        // Update activatedPaymentMethods from the payment-methods response
        // This fixes the case where the initial /store/:merchantId/payment-methods
        // returned a fallback list (just CARD+VIPPS) but the actual Surfboard API
        // response via payment.ts has the full list.
        const serverMethods = (response.data as unknown as Record<string, unknown>).activatedPaymentMethods as string[] | undefined;
        if (serverMethods && serverMethods.length > 0 && merchant) {
          const current = merchant.activatedPaymentMethods || [];
          // Only update if the server returned more methods than we currently have
          if (serverMethods.length > current.length || serverMethods.some(m => !current.includes(m))) {
            useCheckoutStore.getState().setMerchant({
              ...merchant,
              activatedPaymentMethods: serverMethods,
            });
          }
        }

        // Map API method name to frontend method type
        if (preferredMethod) {
          const methodMap: Record<string, Method> = {
            'CARD': 'card', 'VISA': 'card', 'MASTERCARD': 'card',
            'SVIPPS': 'vipps', 'VIPPS': 'vipps',
            'KLARNA': 'klarna',
            'NSWISH': 'swish', 'SSWISH': 'swish', 'SWISH': 'swish',
          };
          const mapped = methodMap[preferredMethod.toUpperCase()] || null;
          if (mapped) {
            setLastUsedMethod(mapped);
            setSelected(mapped);
          }
        }
      } else if (response.error) {
        setError(response.error.message || L.paymentDataError);
      }
    }).catch(() => {
      setError(L.paymentDataError);
    });
  }, [sessionId, surfboardOrderId, setSurfboardData]);

  // Payment status handler
  const handlePaymentStatus = useCallback((data: PaymentStatusData) => {
    const successStatuses = ['PAYMENT_COMPLETED', 'PAYMENT_PROCESSED', 'COMPLETED', 'SUCCESS', 'AUTHORIZED', 'CAPTURED'];
    const statusUpper = (data.paymentStatus || '').toUpperCase();

    if (successStatuses.some(s => statusUpper.includes(s))) {
      setProcessingText(L.completedLabel);
      setTimeout(() => {
        setProcessing(false);
        completeCheckout({
          success: true,
          paymentId: data.paymentId || surfboardOrderId || '',
          orderId: surfboardOrderId || '',
          status: 'COMPLETED',
          paymentMethod: selected?.toUpperCase() || 'CARD',
        });
      }, 500);
    } else if (data.paymentStatus === 'PAYMENT_FAILED') {
      setProcessing(false);
      paymentInFlight.current = false;
      console.error('[SagaPay:Payment] Payment failed:', data.errorMessage || data.failureReason);

      // Two things were wrong here. The message went to `error`, which the
      // banner hides once the order exists, so a declined card produced no
      // visible reaction at all — the customer was returned to the method list
      // with no idea what had happened. And the spent order was left in place,
      // so trying another card was doomed before they typed it.
      setPayError({ message: L.paymentDeclined, canRetry: false });
      rebuildOrderAndCardForm();
    } else if (data.paymentStatus === 'PAYMENT_CANCELLED') {
      setProcessing(false);
      paymentInFlight.current = false;
      setError(null); // Clear any error so user can freely pick another method
    }
  }, [completeCheckout, surfboardOrderId, selected, rebuildOrderAndCardForm, L]);

  // Keep module-level callback forwarders in sync with this component instance.
  // SDK callbacks are registered ONCE (first mount) but must call the CURRENT
  // component's handlers even after unmount/remount. Module-level forwarders
  // bridge the gap so navigating back and forward doesn't break payments.
  useEffect(() => {
    activePaymentStatusHandler = handlePaymentStatus;
    activeErrorHandler = (code: string, msg: string) => {
      // Always reset payment state so user can try another method
      paymentInFlight.current = false;
      setProcessing(false);

      // Don't show error for user-initiated cancellations
      const msgLower = (msg || '').toLowerCase();
      const codeLower = (code || '').toLowerCase();
      const isCancellation = msgLower.includes('cancel') || msgLower.includes('abort') ||
        codeLower.includes('cancel') || codeLower.includes('abort') ||
        code === 'PAYMENT_CANCELLED' || code === 'USER_CANCELLED';

      if (isCancellation) {
        // Don't show error — user just cancelled, let them pick another method
      } else if (mountPhaseDone.current || paymentInFlight.current) {
        setError(msg || L.errorGeneric);
      }
    };
    return () => {
      activePaymentStatusHandler = null;
      activeErrorHandler = null;
    };
  }, [handlePaymentStatus]);

  // =====================================================
  // SDK INIT — single wallet order
  // =====================================================
  useEffect(() => {
    if (globalSdkInitialized) {
      setSdkReady(true);
      setLoading(false);
      // Re-mount card widget if needed (component remounted but SDK is still alive)
      if (!cardMounted.current && window.SurfboardOnlineSDK) {
        // Same retry-tolerant path as first mount. A component remount after a
        // failed payment lands here with the dead iframe still in the
        // container, which is precisely the firstChild crash.
        void mountCardWidget(window.SurfboardOnlineSDK, 'surfboard-card-container')
          .then((ok) => { cardMounted.current = ok; });
      }
      return;
    }
    // Block ALL component instances once any instance has started init
    if (globalSdkInitAttempted) {
      setLoading(false);
      return;
    }
    if (initStarted.current) return;
    if (!window.SurfboardOnlineSDK) { setLoading(false); return; }
    if (!surfboardOrderId || !surfboardNonce || !publicKey) { setLoading(false); return; }

    initStarted.current = true;
    globalSdkInitAttempted = true;   // prevent any other instance from entering
    const SDK = window.SurfboardOnlineSDK;

    // Register callbacks exactly ONCE — SDK rejects re-registration with ON_005
    if (!globalCallbacksRegistered) {
      try {
        SDK.errorCallback((code: string, msg: string) => {
          activeErrorHandler?.(code, msg);
        });
      } catch (e) {
        // Could not register errorCallback
      }
      try {
        SDK.paymentStatusCallback((data: PaymentStatusData) => {
          activePaymentStatusHandler?.(data);
        });
      } catch (e) {
        // Could not register paymentStatusCallback
      }
      globalCallbacksRegistered = true;
    }

    const sdkGlobals = (window as unknown as { g?: { ADD_TERMINAL_CALLBACK?: (cb: (status: string) => void) => void } }).g;
    if (sdkGlobals?.ADD_TERMINAL_CALLBACK) {
      sdkGlobals.ADD_TERMINAL_CALLBACK((status: string) => {
        if (status === 'PAYMENT_COMPLETED' || status === 'PAYMENT_PROCESSED') {
          activePaymentStatusHandler?.({ paymentStatus: status as PaymentStatusData['paymentStatus'] });
        }
      });
    }

    (async () => {
      try {
        // Try initialise — if SDK globals are already set (ON_005), fall back to remountOnlineSDK
        try {
          await SDK.initialiseOnlineSDK({ publicKey, orderId: surfboardOrderId, nonce: surfboardNonce });
        } catch (initErr: unknown) {
          const errMsg = initErr instanceof Error ? initErr.message : String(initErr);
          if (errMsg.includes('ON_005') && typeof SDK.remountOnlineSDK === 'function') {
            await remountSdkOnce(SDK, { publicKey, orderId: surfboardOrderId, nonce: surfboardNonce });
          } else {
            throw initErr;   // not ON_005 — propagate
          }
        }

        // Set card form language based on market locale
        if (SDK.changeLocale) SDK.changeLocale(market.sdkLocale);

        // Add customer info to SDK, phone included.
        //
        // This used to withhold the phone number on the belief that Vipps
        // crashed with one on the order. It does not — Kristoffer confirmed
        // Vipps works better with it — and withholding it left the order
        // without a phone for every method.
        // Not awaited: this is a round-trip to the API that the card form does
        // not depend on, and awaiting it held the mount back by that long for
        // every customer. The same information is sent again, and awaited,
        // immediately before the payment itself — see ensureCustomerOnOrder.
        if (customer && (billingAddress || shippingAddress)) {
          void (async () => {
          try {
            await SDK.order.addCustomerInformation({
              name: sdkName(customer),
              email: customer.email,
              phone: splitPhone(
                typeof customer.phone === 'string' ? customer.phone : '',
                (billingAddress || shippingAddress)?.countryCode
              ),
              // The billing address the customer or the shop supplied — not the
              // delivery address. This slot was filled with the shipping
              // address unconditionally, so a customer who billed one place and
              // shipped to another had the billing address they entered
              // silently replaced on the order. Delivery still falls back into
              // it when there is no separate billing address, which is the
              // common case and what the customer means.
              billingAddress: sdkAddress(billingAddress || shippingAddress),
            });
          } catch (e) {
            // Failed to add customer info — non-critical
          }
          })();
        }

        // Mount card widget IMMEDIATELY after init — Surfboard SDK
        // requires mount() right after initialiseOnlineSDK().
        // The container is always in the DOM (display:none when not selected).
        // Retry-tolerant, and clears the container first — see mountCardWidget.
        cardMounted.current = await mountCardWidget(SDK, 'surfboard-card-container');

        globalSdkInitialized = true;
        setSdkReady(true);
      } catch (err) {
        const errMsg = err instanceof Error ? err.message : String(err);
        console.error('[SagaPay:Payment] SDK init FAILED:', errMsg, err);

        // The SDK is domain-locked, so the host it is running on is the first
        // thing worth knowing when init fails. It used to read the SDK's own
        // globals, but those are only populated after a successful init — so on
        // the one path where the log mattered it always printed "undefined".
        console.error('[SagaPay:Payment] kjører på:', window.location.host);

        // ── Expired / invalid order recovery ────────────────
        // If Surfboard says the link/order is expired or invalid,
        // auto-retry with a fresh order up to MAX_RETRIES times.
        // After that, show a user-friendly retry button.
        if (errMsg.includes('Invalid or Expired Link') && retryCount.current < MAX_RETRIES) {
          retryCount.current += 1;
          globalSdkInitialized = false;
          globalSdkInitAttempted = false;
          initStarted.current = false;
          // Clear surfboard data → triggers lazy-fetch useEffect
          // which calls /methods/?force=1 to create a fresh order
          setSurfboardData('', '', '');
        } else {
          setError(L.paymentSystemError);
        }
      }
      setLoading(false);
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [surfboardOrderId, surfboardNonce, publicKey, customer, shippingAddress]);

  // =====================================================
  // MOUNT WALLET BUTTONS (Apple Pay + Google Pay)
  // =====================================================
  useEffect(() => {
    if (!sdkReady || loading || walletMounted.current) return;
    if (!hasApplePay && !hasGooglePay) { setWalletCheckDone(true); return; }

    const SDK = window.SurfboardOnlineSDK;
    if (!SDK) return;

    const timeoutIds: ReturnType<typeof setTimeout>[] = [];

    const doMount = () => {
      // Mount Apple Pay and Google Pay SEPARATELY so one failing doesn't block the other.
      /**
       * Say why the wallet did not appear.
       *
       * Both mounts swallowed their error, so an Apple Pay that refused to
       * render — unregistered domain, no merchant identifier for this
       * environment, a browser that cannot offer it — looked exactly like an
       * Apple Pay that had simply not been switched on. There was nothing in
       * the console to tell the two apart, and the first person to hit it in
       * live had no thread to pull.
       *
       * The button is still hidden either way; the customer sees no error.
       * This is for whoever is asked why it did not work.
       */
      if (hasApplePay && document.getElementById('sdk-applepay-button')) {
        try {
          SDK.mount({ mountApplePayWidget: 'sdk-applepay-button' });
        } catch (e) {
          console.warn(
            '[SagaPay:Payment] Apple Pay ble ikke montert på', window.location.host + ':',
            e instanceof Error ? e.message : e,
            '— sjekk at domenet er registrert for Apple Pay hos Surfboard, og at terminalen har det aktivert i dette miljøet.'
          );
        }
      }
      if (hasGooglePay && document.getElementById('sdk-googlepay-button')) {
        try {
          SDK.mount({ mountGooglePayWidget: 'sdk-googlepay-button' });
        } catch (e) {
          console.warn(
            '[SagaPay:Payment] Google Pay ble ikke montert:',
            e instanceof Error ? e.message : e
          );
        }
      }
      walletMounted.current = true;

      // Google Pay post-mount: force the rendered button to 100% × 48px.
      // Google’s createButton() returns a <div> containing a <button class="gpay-button">.
      // We style from JS because CSS rules get overridden by Google’s inline styles.
      const styleGooglePay = () => {
        const container = document.getElementById('sdk-googlepay-button');
        if (!container) return;
        // Force fixed 48px height on container and ALL children
        container.style.setProperty('height', '48px', 'important');
        container.style.setProperty('max-height', '48px', 'important');
        container.style.setProperty('overflow', 'hidden', 'important');
        // Style every child element inside the container
        const allChildren = container.querySelectorAll('*');
        allChildren.forEach(el => {
          const htmlEl = el as HTMLElement;
          htmlEl.style.setProperty('width', '100%', 'important');
          htmlEl.style.setProperty('max-width', '100%', 'important');
          htmlEl.style.setProperty('min-width', '0', 'important');
          htmlEl.style.setProperty('height', '48px', 'important');
          htmlEl.style.setProperty('max-height', '48px', 'important');
        });
        // Find the gpay-button specifically
        const gpayBtn = container.querySelector('.gpay-button, button, [class*="gpay"]') as HTMLElement;
        if (gpayBtn) {
          gpayBtn.style.setProperty('height', '48px', 'important');
          gpayBtn.style.setProperty('max-height', '48px', 'important');
          gpayBtn.style.setProperty('width', '100%', 'important');
          gpayBtn.style.setProperty('border-radius', '8px', 'important');
        }
      };
      // Google Pay renders async — poll a few times
      [100, 300, 600, 1000, 2000].forEach(d => {
        const tid = setTimeout(styleGooglePay, d);
        timeoutIds.push(tid);
      });

      // Observe for rendered content
      const observer = new MutationObserver((mutations) => {
        for (const m of mutations) {
          if (m.addedNodes.length > 0) {
            const t = m.target as HTMLElement;
            if (t.id === 'sdk-applepay-button' || t.closest?.('#sdk-applepay-button')) setApplePayAvailable(true);
            if (t.id === 'sdk-googlepay-button' || t.closest?.('#sdk-googlepay-button')) setGooglePayAvailable(true);
          }
        }
      });
      const aEl = document.getElementById('sdk-applepay-button');
      const gEl = document.getElementById('sdk-googlepay-button');
      if (aEl) observer.observe(aEl, { childList: true, subtree: true });
      if (gEl) observer.observe(gEl, { childList: true, subtree: true });

      [500, 1500, 3000, 5000].forEach(delay => {
        const tid = setTimeout(() => {
          const a = document.getElementById('sdk-applepay-button');
          const g = document.getElementById('sdk-googlepay-button');
          if (a && a.children.length > 0) setApplePayAvailable(true);
          if (g && g.children.length > 0) setGooglePayAvailable(true);
        }, delay);
        timeoutIds.push(tid);
      });

      const finalTid = setTimeout(() => {
        observer.disconnect();
        setWalletCheckDone(true);
        mountPhaseDone.current = true;

        /**
         * If Apple Pay never appeared, say which precondition failed.
         *
         * The SDK does not throw when it gives up — it returns quietly and
         * reports the reason to its own telemetry, which we cannot read. So a
         * missing button looks the same whether the domain is unregistered,
         * the browser cannot do Apple Pay, or Apple's own script never loaded.
         *
         * These are the exact checks mountApplePay makes, read from the same
         * globals, so the console answers the question directly instead of
         * everyone guessing at it in turn.
         */
        if (hasApplePay && !document.getElementById('sdk-applepay-button')?.children.length) {
          const w = window as unknown as Record<string, unknown>;
          const session = w.ApplePaySession as { canMakePayments?: () => boolean } | undefined;
          console.warn('[SagaPay:ApplePay] Ingen knapp. Forutsetningene SDK-en sjekker:', {
            ApplePaySession: session ? 'finnes' : 'MANGLER — kun Safari på macOS/iOS',
            canMakePayments: session?.canMakePayments ? session.canMakePayments() : 'n/a',
            ApplePaySDK: w.ApplePaySDK ? 'lastet' : 'MANGLER — apple-pay-sdk.js lastet ikke',
            host: window.location.host,
            iframe: window.self !== window.top,
          });
        }
      }, 6000);
      timeoutIds.push(finalTid);
    };

    const rafTimeout = setTimeout(doMount, 50);
    timeoutIds.push(rafTimeout);

    return () => {
      timeoutIds.forEach(id => clearTimeout(id));
    };
  }, [sdkReady, loading, hasApplePay, hasGooglePay]);

  // =====================================================
  // MOUNT CARD FORM — backup: re-mount if not already mounted
  // Primary mount happens immediately after SDK init above.
  // This effect handles remount after navigating back/forward.
  // =====================================================
  useEffect(() => {
    if (!sdkReady || selected !== 'card') return;
    if (cardMounted.current) return;

    const SDK = window.SurfboardOnlineSDK;
    if (!SDK) return;

    // Selecting card after the step is already up. Same routine as the other
    // two mount sites so the container is always cleared and the element
    // always looked up fresh — the two causes of the firstChild crash.
    let cancelled = false;
    void mountCardWidget(SDK, 'surfboard-card-container').then((ok) => {
      if (!cancelled) cardMounted.current = ok;
    });
    return () => { cancelled = true; };
  }, [sdkReady, selected]);

  // Center Vipps popup iframe + proper backdrop + hide conflicting elements
  useEffect(() => {
    let intervalId: ReturnType<typeof setInterval> | null = null;
    let timeoutId: ReturnType<typeof setTimeout> | null = null;
    let backdrop: HTMLElement | null = null;

    const showVippsBackdrop = () => {
      if (backdrop) return;
      backdrop = document.createElement('div');
      backdrop.id = 'sagapay-vipps-backdrop';
      backdrop.style.cssText = 'position:fixed;top:0;left:0;right:0;bottom:0;background:rgba(0,0,0,0.6);z-index:999990;pointer-events:auto;';
      document.body.appendChild(backdrop);
      // Hide Google Pay button so it can't bleed through overlay
      const gpay = document.getElementById('sdk-googlepay-button');
      if (gpay) gpay.style.setProperty('visibility', 'hidden', 'important');
      // Also hide any Google Pay body-level elements
      document.querySelectorAll<HTMLElement>('body > div[id*="gpay"], body > div[class*="gpay"], body > div[id*="google-pay"]').forEach(el => {
        el.style.setProperty('display', 'none', 'important');
      });
    };

    const removeVippsBackdrop = () => {
      if (backdrop && backdrop.parentElement) {
        backdrop.parentElement.removeChild(backdrop);
        backdrop = null;
      }
      // Restore Google Pay button
      const gpay = document.getElementById('sdk-googlepay-button');
      if (gpay) gpay.style.removeProperty('visibility');
    };

    const centerVippsIframe = () => {
      let foundVipps = false;
      document.querySelectorAll('iframe').forEach((iframe) => {
        if (iframe.src?.includes('vipps')) {
          foundVipps = true;
          showVippsBackdrop();
          iframe.style.setProperty('position', 'fixed', 'important');
          iframe.style.setProperty('top', '50%', 'important');
          iframe.style.setProperty('left', '50%', 'important');
          iframe.style.setProperty('transform', 'translate(-50%, -50%)', 'important');
          iframe.style.setProperty('width', '420px', 'important');
          iframe.style.setProperty('height', '600px', 'important');
          iframe.style.setProperty('max-width', '95vw', 'important');
          iframe.style.setProperty('max-height', '90vh', 'important');
          iframe.style.setProperty('z-index', '999999', 'important');
          iframe.style.setProperty('border-radius', '16px', 'important');
          iframe.style.setProperty('box-shadow', '0 25px 50px rgba(0,0,0,0.5)', 'important');
          let parent = iframe.parentElement;
          while (parent && parent !== document.body) {
            if (parent.style.position === 'absolute' || parent.style.position === 'fixed') {
              parent.style.setProperty('position', 'fixed', 'important');
              parent.style.setProperty('top', '0', 'important');
              parent.style.setProperty('left', '0', 'important');
              parent.style.setProperty('right', '0', 'important');
              parent.style.setProperty('bottom', '0', 'important');
              parent.style.setProperty('width', '100%', 'important');
              parent.style.setProperty('height', '100%', 'important');
              parent.style.setProperty('display', 'flex', 'important');
              parent.style.setProperty('align-items', 'center', 'important');
              parent.style.setProperty('justify-content', 'center', 'important');
              parent.style.setProperty('z-index', '999998', 'important');
              parent.style.setProperty('background', 'transparent', 'important');
            }
            parent = parent.parentElement;
          }
        }
      });
      // If no Vipps iframe found anymore, remove backdrop
      if (!foundVipps && backdrop) {
        removeVippsBackdrop();
        if (intervalId) { clearInterval(intervalId); intervalId = null; }
      }
    };
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node instanceof HTMLElement) {
            if (node.querySelector?.('iframe[src*="vipps"]') || (node instanceof HTMLIFrameElement && node.src?.includes('vipps'))) {
              if (intervalId) clearInterval(intervalId);
              intervalId = setInterval(centerVippsIframe, 100);
              centerVippsIframe();
              if (timeoutId) clearTimeout(timeoutId);
              timeoutId = setTimeout(() => { if (intervalId) { clearInterval(intervalId); intervalId = null; } removeVippsBackdrop(); }, 30000);
            }
          }
        });
        // Also watch for removed Vipps iframes
        mutation.removedNodes.forEach((node) => {
          if (node instanceof HTMLElement) {
            if (node instanceof HTMLIFrameElement && node.src?.includes('vipps')) {
              removeVippsBackdrop();
            } else if (node.querySelector?.('iframe[src*="vipps"]')) {
              removeVippsBackdrop();
            }
          }
        });
      });
    });
    observer.observe(document.body, { childList: true, subtree: true });
    return () => { observer.disconnect(); if (intervalId) clearInterval(intervalId); if (timeoutId) clearTimeout(timeoutId); removeVippsBackdrop(); };
  }, []);

  // =====================================================
  // PAY
  // =====================================================
  const handlePay = async () => {
    if (!selected) { setError(L.selectMethodError); return; }
    if (paymentInFlight.current) return; // Prevent double-click
    paymentInFlight.current = true;

    // Self-healing guard. Every abort path is supposed to release this, but a
    // pure boolean that one path forgets leaves the pay button permanently
    // dead with no way back for the customer. Time-bound it so the worst case
    // is a delay, not a checkout they cannot finish.
    if (inFlightWatchdog.current) clearTimeout(inFlightWatchdog.current);
    inFlightWatchdog.current = setTimeout(() => {
      if (paymentInFlight.current) {
        console.warn('[SagaPay:Payment] Releasing stuck payment guard after 90s');
        paymentInFlight.current = false;
        setProcessing(false);
      }
    }, 90000);

    setProcessing(true);
    setProcessingText(L.processingLabel);
    setError(null);
    setPayError(null);

    try {
      const SDK = window.SurfboardOnlineSDK;

      // Methods that finish on the provider's own page — Vipps, Klarna, Swish,
      // MobilePay — are started server-side, always.
      //
      // They need no card fields and no SDK. Going through the SDK meant asking
      // it to initiate a method the *order* does not list: the order comes back
      // with supportedPaymentMethods ["CARD"], so `initiatePayments('SVIPPS')`
      // answered ON_010 ("this payment method is not supported for this
      // payment") and the customer's Vipps click did nothing at all. It only
      // ever appeared to work because the SDK fails to initialise in headless
      // browsers, which pushed our tests down the server path a real customer
      // never took.
      const REDIRECT_METHOD: Record<string, string> = {
        vipps: 'SVIPPS', klarna: 'KLARNA', mobilepay: 'SMOBILEPAY',
      };

      // Swish is not in the list above because its desktop flow is a QR code
      // the SDK renders from the payment attempt; there is nothing to redirect
      // to. It still falls back to the server when the SDK is unusable.
      const SDK_UNUSABLE = !sdkReady || !SDK?.order;
      const serverMethod =
        REDIRECT_METHOD[selected as string] ??
        (SDK_UNUSABLE && selected === 'swish' ? 'NSWISH' : undefined);

      if (serverMethod && sessionId) {
        const started = await startRedirectPayment(sessionId, serverMethod);
        const plan = started.data;

        if (plan?.kind === 'url' && plan.url) {
          window.location.href = plan.url;
          return;                       // leaving the page — keep paymentInFlight
        }

        if (plan?.kind === 'form' && plan.url && plan.fields) {
          // Signed provider fields must arrive as a POST, so submit a form
          // rather than trying to fold them into a URL.
          const form = document.createElement('form');
          form.method = 'POST';
          form.action = plan.url;
          for (const [name, value] of Object.entries(plan.fields)) {
            const input = document.createElement('input');
            input.type = 'hidden';
            input.name = name;
            input.value = value;
            form.appendChild(input);
          }
          document.body.appendChild(form);
          form.submit();
          return;
        }

        if (started.error?.code === 'METHOD_UNAVAILABLE') {
          const dead = started.error.method;
          if (dead) setUnavailableMethods(prev => prev.includes(dead) ? prev : [...prev, dead]);
          methodUnavailable.current = true;
          setSelected(null);
          throw new Error(started.error.message);
        }

        throw new Error(started.error?.message || L.paymentSystemError);
      }

      if (!SDK?.order) throw new Error(L.paymentSystemError);

      /**
       * `initiatePayments` resolves with `PaymentAttempt | Error` — it does not
       * throw. Awaiting it and discarding the result meant a refusal like
       * ON_009 ("phone number required") vanished silently: no payment was
       * created, no message shown, and the customer sat on a payment step where
       * the button appeared to do nothing.
       */
      const startSdkPayment = async (method: SurfboardPaymentMethod) => {
        const attempt = await SDK.order.initiatePayments(method);
        const failure = attempt as unknown as { code?: string; message?: string } | undefined;
        if (failure && failure.code && !('getPaymentStatus' in (attempt as object))) {
          console.error('[SagaPay:Payment] initiatePayments avvist:', failure.code, failure.message);
          throw new Error(failure.message || L.errorGeneric);
        }
        return attempt;
      };

      /**
       * Card requires email, phone and billing address on the order, per
       * Surfboard's payment-flow rules. We deliberately leave phone off at
       * order creation because Vipps fails when it is present, so for card it
       * has to be supplied here — otherwise every card payment stops at ON_009.
       */
      const ensureCustomerOnOrder = async () => {
        const forBilling = billingAddress || shippingAddress;
        const phone = splitPhone(
          typeof customer?.phone === 'string' ? customer.phone : '',
          forBilling?.countryCode
        );
        if (!phone) return;

        try {
          await SDK.order.addCustomerInformation({
            name: sdkName(customer),
            email: customer?.email,
            phone,
            billingAddress: sdkAddress(forBilling),
          });
        } catch (customerError) {
          // Non-fatal (ON_008): let the payment attempt report the real problem.
          console.warn('[SagaPay:Payment] addCustomerInformation feilet:', customerError);
        }
      };

      if (selected === 'card') {
        await ensureCustomerOnOrder();
        await startSdkPayment('CARD');
      // No branches for vipps, klarna or mobilepay: they returned above,
      // started server-side. Asking the SDK to initiate them only ever produced
      // ON_010, because the order supports CARD alone.
      } else if (selected === 'swish') {
        // Swish requires manual handling: QR code (desktop) or app redirect (mobile)
        const paymentAttempt = await SDK.order.initiatePayments('NSWISH');

        const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

        if (isMobile && paymentAttempt?.getSwishAppRedirectUrl) {
          // Mobile: redirect to Swish app
          const callbackUrl = window.location.href;
          const redirectUrl = typeof paymentAttempt.getSwishAppRedirectUrl === 'function'
            ? paymentAttempt.getSwishAppRedirectUrl(callbackUrl)
            : paymentAttempt.getSwishAppRedirectUrl;
          if (redirectUrl) {
            window.location.href = redirectUrl;
            return; // Don't reset paymentInFlight — we're leaving the page
          }
        }

        // Desktop: use QR code image directly from Surfboard response
        // Surfboard returns nswishQRDataUrl as a ready-made data:image/png;base64 URL
        const qrImageUrl = paymentAttempt?.nswishQRDataUrl;

        if (qrImageUrl) {
          setSwishQRUrl(qrImageUrl);
        } else {
          setError(L.errorGeneric);
          paymentInFlight.current = false;
        }
        setProcessing(false);
      }
    } catch (err: unknown) {
      console.error('[SagaPay:Payment] Pay error:', err);

      // Show what actually went wrong. Overwriting every failure with the
      // generic text meant a precise, actionable message — "Klarna er ikke
      // tilgjengelig i denne butikken. Velg en annen betalingsmåte." — was
      // thrown away and replaced with one that told the customer nothing.
      const message = err instanceof Error && err.message ? err.message : L.errorGeneric;
      const methodGone = methodUnavailable.current;
      methodUnavailable.current = false;

      // Retrying a method the store does not support cannot work; the way
      // forward is to pick another one.
      setPayError({ message, canRetry: !methodGone });
      setProcessing(false);
      paymentInFlight.current = false;
    }
  };

  // Total
  const cartAmountMinor = cart?.totalAmount?.amount ?? 0;
  const shippingAmountMinor = selectedShipping?.price?.amount ?? 0;
  const total = (cartAmountMinor + shippingAmountMinor) / 100;
  const currency = cart?.totalAmount?.currency || 'NOK';

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.loading}>
          <div className={styles.spinner} />
          <p>{L.loadingLabel}</p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      {processing && (
        <div className={styles.overlay}>
          <div className={styles.modal}>
            <div className={styles.spinner} />
            <h3>{processingText}</h3>
            <p>{L.waitMessage}</p>
          </div>
        </div>
      )}

      {/* Swish QR code modal (desktop) */}
      {swishQRUrl && (
        <div className={styles.overlay}>
          <div className={styles.modal}>
            <img src={LOGOS.swish} alt="Swish" style={{ width: 80, marginBottom: 12 }} />
            <h3>{locale === 'sv' ? 'Skanna QR-koden med Swish' : 'Scan QR code with Swish'}</h3>
            <p style={{ fontSize: '0.85rem', opacity: 0.7, marginBottom: 12 }}>
              {locale === 'sv' ? 'Öppna Swish-appen och skanna koden nedan' : 'Open the Swish app and scan the code below'}
            </p>
            <img src={swishQRUrl} alt="Swish QR" style={{ width: 280, height: 280, borderRadius: 12 }} />
            <button
              onClick={() => { setSwishQRUrl(null); paymentInFlight.current = false; }}
              style={{ marginTop: 16, padding: '8px 24px', borderRadius: 8, border: '1px solid #ddd', background: 'white', cursor: 'pointer', fontSize: '0.9rem' }}
            >
              {L.backButton}
            </button>
          </div>
        </div>
      )}

      <h2 className={styles.title}>{L.paymentTitle}</h2>

      {/* Only warn when the customer is actually blocked.
          A load-time hiccup that later resolved used to leave this banner —
          with a retry button — sitting above a payment step that had the SDK
          data and every method rendered, telling them their order was broken
          when they could pay perfectly well. Once processing starts, errors
          are always shown. */}
      {/* A failure from pressing Pay. Always shown — the customer is stuck
          until they read it. */}
      {payError && (
        <div className={styles.error}>
          {payError.message}
          {payError.canRetry && (
            <button onClick={handleManualRetry} className={styles.retryButton}>
              {L.retryButton || 'Prøv igjen'}
            </button>
          )}
        </div>
      )}

      {error && !payError && (processing || !surfboardOrderId) && (
        <div className={styles.error}>
          {error}
          {/* This is the customer's way out of a failed payment, so it needs a
              full-size tap target — 6px of padding made it 32px tall, and it
              only ever appears when something has already gone wrong. */}
          <button
            onClick={handleManualRetry}
            className={styles.retryButton}
          >
            {L.retryButton || 'Prøv igjen'}
          </button>
        </div>
      )}

      {/* SDK-rendered wallet buttons — side by side at the top */}
      {(hasApplePay || hasGooglePay) && (
        <div
          className={styles.walletButtons}
          style={walletCheckDone && !applePayAvailable && !googlePayAvailable ? { display: 'none' } : undefined}
        >
          {hasApplePay && (
            <div
              id="sdk-applepay-button"
              className={styles.walletButton}
              style={walletCheckDone && !applePayAvailable ? { display: 'none' } : undefined}
            />
          )}
          {hasGooglePay && (
            <div
              id="sdk-googlepay-button"
              className={styles.walletButton}
              style={walletCheckDone && !googlePayAvailable ? { display: 'none' } : undefined}
            />
          )}
        </div>
      )}

      {(applePayAvailable || googlePayAvailable) && (hasCard || hasVipps || hasKlarna || hasSwish) && (
        <div className={styles.divider}><span>{L.orPayWith}</span></div>
      )}

      <div className={styles.methods} role="radiogroup" aria-label={L.paymentTitle}>
        {(() => {
          const availableMethods: Method[] = [];
          if (hasCard) availableMethods.push('card');
          if (hasVipps) availableMethods.push('vipps');
          if (hasSwish) availableMethods.push('swish');
          if (hasKlarna) availableMethods.push('klarna');

          // Move last used method to the top
          if (lastUsedMethod && availableMethods.includes(lastUsedMethod)) {
            const idx = availableMethods.indexOf(lastUsedMethod);
            if (idx > 0) {
              availableMethods.splice(idx, 1);
              availableMethods.unshift(lastUsedMethod);
            }
          }

          return availableMethods.map(method => {
            // Each method row is a radio and handles Enter/Space itself. The
            // handlers bail out unless the event started on the row: the card
            // fields live inside this element, so the row was calling
            // preventDefault() on every space bar pressed in them — a
            // cardholder typing "Ola Nordmann" got "OlaNordmann" on the
            // transaction, and it looked like an SDK defect until the same
            // bundle was tried outside our page.
            if (method === 'card') return (
            <div key="card" className={`${styles.method} ${selected === 'card' ? styles.selected : ''}`} role="radio" aria-checked={selected === 'card'} tabIndex={0} onKeyDown={(e) => { if (e.target !== e.currentTarget) return; if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setSelected('card'); setError(null); setPayError(null); paymentInFlight.current = false; setProcessing(false); } }}>
                <div className={styles.methodHeader} onClick={() => { setSelected('card'); setError(null); setPayError(null); paymentInFlight.current = false; setProcessing(false); }}>
                  <div className={styles.methodInfo}>
                    <div className={`${styles.radio} ${selected === 'card' ? styles.radioChecked : ''}`} />
                    <span className={styles.methodName}>{L.cardLabel}</span>
                    {lastUsedMethod === 'card' && <span className={styles.lastUsedBadge}>{L.lastUsedLabel}</span>}
                  </div>
                  <div className={styles.logos}>
                    <img src={LOGOS.visa} alt="Visa" />
                    <img src={LOGOS.mastercard} alt="Mastercard" />
                  </div>
                </div>
                <div className={styles.cardDropdown} style={{ display: selected === 'card' ? 'block' : 'none' }}>
                  {/* spellCheck is set on our own container, not on the SDK's
                      inputs, and inherits down: the browser was drawing a red
                      squiggle under every cardholder name. */}
                  <div id="surfboard-card-container" className={styles.cardFormInline} spellCheck={false} />
                </div>
              </div>
            );
            if (method === 'vipps') return (
              <div key="vipps" className={`${styles.method} ${selected === 'vipps' ? styles.selected : ''}`} role="radio" aria-checked={selected === 'vipps'} tabIndex={0} onClick={() => { setSelected('vipps'); setError(null); setPayError(null); paymentInFlight.current = false; setProcessing(false); cardMounted.current = false; }} onKeyDown={(e) => { if (e.target !== e.currentTarget) return; if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setSelected('vipps'); setError(null); setPayError(null); paymentInFlight.current = false; setProcessing(false); cardMounted.current = false; } }}>
                <div className={styles.methodHeader}>
                  <div className={styles.methodInfo}>
                    <div className={`${styles.radio} ${selected === 'vipps' ? styles.radioChecked : ''}`} />
                    <span className={styles.methodName}>{L.vippsLabel}</span>
                    {lastUsedMethod === 'vipps' && <span className={styles.lastUsedBadge}>{L.lastUsedLabel}</span>}
                  </div>
                  <div className={styles.logos}><img src={LOGOS.vipps} alt="Vipps" /></div>
                </div>
              </div>
            );
            if (method === 'klarna') return (
              <div key="klarna" className={`${styles.method} ${selected === 'klarna' ? styles.selected : ''}`} role="radio" aria-checked={selected === 'klarna'} tabIndex={0} onClick={() => { setSelected('klarna'); setError(null); setPayError(null); paymentInFlight.current = false; setProcessing(false); cardMounted.current = false; }} onKeyDown={(e) => { if (e.target !== e.currentTarget) return; if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setSelected('klarna'); setError(null); setPayError(null); paymentInFlight.current = false; setProcessing(false); cardMounted.current = false; } }}>
                <div className={styles.methodHeader}>
                  <div className={styles.methodInfo}>
                    <div className={`${styles.radio} ${selected === 'klarna' ? styles.radioChecked : ''}`} />
                    <span className={styles.methodName}>{L.klarnaLabel}</span>
                    {lastUsedMethod === 'klarna' && <span className={styles.lastUsedBadge}>{L.lastUsedLabel}</span>}
                  </div>
                  <div className={styles.logos}><img src={LOGOS.klarna} alt="Klarna" /></div>
                </div>
              </div>
            );
            if (method === 'swish') return (
              <div key="swish" className={`${styles.method} ${selected === 'swish' ? styles.selected : ''}`} role="radio" aria-checked={selected === 'swish'} tabIndex={0} onClick={() => { setSelected('swish'); setError(null); setPayError(null); paymentInFlight.current = false; setProcessing(false); cardMounted.current = false; }} onKeyDown={(e) => { if (e.target !== e.currentTarget) return; if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setSelected('swish'); setError(null); setPayError(null); paymentInFlight.current = false; setProcessing(false); cardMounted.current = false; } }}>
                <div className={styles.methodHeader}>
                  <div className={styles.methodInfo}>
                    <div className={`${styles.radio} ${selected === 'swish' ? styles.radioChecked : ''}`} />
                    <span className={styles.methodName}>{L.swishLabel}</span>
                    {lastUsedMethod === 'swish' && <span className={styles.lastUsedBadge}>{L.lastUsedLabel}</span>}
                  </div>
                  <div className={styles.logos}><img src={LOGOS.swish} alt="Swish" /></div>
                </div>
              </div>
            );
            return null;
          });
        })()}
      </div>

      {/* Legally required: terms & privacy acceptance text */}
      {(merchant?.storeInfo?.termsAndConditionsURL || merchant?.storeInfo?.privacyPolicyURL) && (
        <p className={styles.termsAcceptance}>
          {L.termsPrefix}{' '}
          {merchant.storeInfo.termsAndConditionsURL && (
            <a
              href={merchant.storeInfo.termsAndConditionsURL}
              target="_blank"
              rel="noopener noreferrer"
            >
              {L.termsLink}
            </a>
          )}
          {merchant.storeInfo.termsAndConditionsURL && merchant.storeInfo.privacyPolicyURL && (
            <>{' '}{L.andWord}{' '}</>
          )}
          {merchant.storeInfo.privacyPolicyURL && (
            <a
              href={merchant.storeInfo.privacyPolicyURL}
              target="_blank"
              rel="noopener noreferrer"
            >
              {L.privacyLink}
            </a>
          )}
          .
        </p>
      )}

      <div className={styles.actions}>
        <Button variant="secondary" onClick={() => prevStep()}>{L.backButton}</Button>
        <Button onClick={handlePay} disabled={!selected || processing}>
          {L.payButton} {formatAmount(Math.round(total * 100), numberLocaleFor(market.sdkLocale))} {currency}
        </Button>
      </div>

      <p className={styles.secure}>{L.securePayment}</p>
    </div>
  );
}
